import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, TextField, InputAdornment, Button, Typography,
  Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Avatar, Divider, CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AppleIcon from '@mui/icons-material/Apple';
import { getGroupedRequestLogsApi, getUserDeviceLogsApi } from '../../services/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import PageHeader from '../../components/PageHeader';

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  }).format(d);
};

const fmtDateShort = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(d);
};

const METHOD_COLORS = {
  GET: { bg: '#e8f5e9', text: '#2e7d32' },
  POST: { bg: '#e3f2fd', text: '#1565c0' },
  PATCH: { bg: '#fff8e1', text: '#f57f17' },
  PUT: { bg: '#fff3e0', text: '#e65100' },
  DELETE: { bg: '#fce4ec', text: '#c62828' },
};

const MethodChip = ({ value }) => {
  const colors = METHOD_COLORS[value?.toUpperCase()] || { bg: '#f5f5f5', text: '#616161' };
  return (
    <Box sx={{
      px: 1, py: 0.25, borderRadius: 1, fontSize: 11, fontWeight: 700,
      bgcolor: colors.bg, color: colors.text, display: 'inline-block', letterSpacing: 0.5,
    }}>
      {value || '—'}
    </Box>
  );
};

const DeviceIcon = ({ osName }) => {
  const os = (osName || '').toLowerCase();
  if (os.includes('ios')) return <AppleIcon sx={{ fontSize: 16, color: '#555' }} />;
  return <PhoneAndroidIcon sx={{ fontSize: 16, color: '#555' }} />;
};

// strip the massive device-info query params, keep only the clean path
const cleanEndpoint = (endpoint) => {
  if (!endpoint) return '—';
  try {
    const url = new URL('http://x' + endpoint);
    return url.pathname;
  } catch {
    return endpoint.split('?')[0];
  }
};

// ─── Grouped table columns ───────────────────────────────────────────────────

const GROUPED_COLUMNS = [
  {
    field: 'user', headerName: 'User', flex: 1, minWidth: 180, sortable: false,
    renderCell: (p) => {
      const u = p.row.user;
      const name = u?.name || u?.userName || '—';
      const email = u?.email || '';
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
          <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: '#6366f1', flexShrink: 0 }}>
            {name[0]?.toUpperCase() || '?'}
          </Avatar>
          <Box sx={{ overflow: 'hidden', lineHeight: "normal" }}>
            <Typography variant="body2" fontWeight={500} noWrap>{name}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>{email}</Typography>
          </Box>
        </Box>
      );
    },
  },
  {
    field: 'device', headerName: 'Device', flex: 1, minWidth: 160, sortable: false,
    renderCell: (p) => {
      const { brand, modelName, osName, osVersion } = p.row;
      const deviceLabel = modelName || 'Unknown Device';
      const osLabel = osName ? `${osName} ${osVersion || ''}`.trim() : '';
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          <DeviceIcon osName={osName} />
          <Box sx={{ overflow: 'hidden', lineHeight: "normal" }}>
            <Typography variant="body2" noWrap>{deviceLabel}</Typography>
            {(brand || osLabel) && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {[brand, osLabel].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
        </Box>
      );
    },
  },
  {
    field: 'count', headerName: 'Requests', width: 100, sortable: false,
    renderCell: (p) => (
      <Chip label={p.value?.toLocaleString()} size="small" sx={{ bgcolor: '#f0f0ff', color: '#6366f1', fontWeight: 600 }} />
    ),
  },
  {
    field: 'userType', headerName: 'Type', width: 110, sortable: false,
    renderCell: (p) => p.value
      ? <Chip label={p.value} size="small" variant="outlined" />
      : '—',
  },
  {
    field: 'ip', headerName: 'IP', width: 140, sortable: false,
    renderCell: (p) => (
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{p.value || '—'}</Typography>
    ),
  },
  {
    field: 'latestTimestamp', headerName: 'Last Seen', width: 180,
    sortingOrder: ['desc', 'asc', null],
    renderCell: (p) => (
      <Typography variant="caption" color="text.secondary">{fmtDate(p.value)}</Typography>
    ),
  },
];

// ─── Log Detail Modal ────────────────────────────────────────────────────────

function LogDetailModal({ group, open, onClose }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 30;

  const fetchLogs = useCallback(async () => {
    if (!group) return;
    setLoading(true);
    try {
      const { data } = await getUserDeviceLogsApi({
        userId: group.userId,
        modelName: group.modelName,
        page,
        size: PAGE_SIZE,
      });
      setLogs(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [group, page]);

  useEffect(() => {
    if (open) { setPage(1); setLogs([]); }
  }, [open, group]);

  useEffect(() => {
    if (open) fetchLogs();
  }, [open, fetchLogs]);

  if (!group) return null;

  const u = group.user;
  const userName = u?.name || u?.userName || '—';
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '85vh' } }}>

      {/* Header */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>{userName}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
              <DeviceIcon osName={group.osName} />
              <Typography variant="body2" color="text.secondary">
                {[group.brand, group.modelName].filter(Boolean).join(' ')}
              </Typography>
              {group.osName && (
                <Chip label={`${group.osName} ${group.osVersion || ''}`.trim()} size="small" variant="outlined" />
              )}
              <Chip label={`${total.toLocaleString()} requests`} size="small"
                sx={{ bgcolor: '#f0f0ff', color: '#6366f1', fontWeight: 600 }} />
            </Box>
            {(u?.email || u?.phoneNumber) && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {[u?.email, u?.phoneNumber].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      {/* Log list */}
      <DialogContent sx={{ p: 0 }}>
        {loading && logs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            <Typography>No logs found</Typography>
          </Box>
        ) : (
          <Box>
            {logs.map((log, idx) => (
              <Box key={log._id}>
                <Box sx={{
                  px: 3, py: 1.5, display: 'flex', gap: 2, alignItems: 'flex-start',
                  '&:hover': { bgcolor: '#fafafa' },
                }}>
                  {/* Time column */}
                  <Box sx={{ width: 130, flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
                      {fmtDateShort(log.timestamp)}
                    </Typography>
                  </Box>

                  {/* Method */}
                  <Box sx={{ width: 60, flexShrink: 0, pt: 0.25 }}>
                    <MethodChip value={log.method} />
                  </Box>

                  {/* Endpoint */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Tooltip title={log.endpoint || ''} placement="top">
                      <Typography variant="body2" sx={{
                        fontFamily: 'monospace', fontSize: 12,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {cleanEndpoint(log.endpoint)}
                      </Typography>
                    </Tooltip>
                    {log.ip && (
                      <Typography variant="caption" color="text.secondary">
                        {log.ip}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {idx < logs.length - 1 && <Divider sx={{ mx: 3, opacity: 0.5 }} />}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      {/* Pagination footer */}
      {totalPages > 1 && (
        <>
          <Divider />
          <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Page {page} of {totalPages} · {total.toLocaleString()} total
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function UserLogs() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // eslint-disable-next-line
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, size: pageSize };
      if (search) params.search = search;
      if (userTypeFilter) params.userType = userTypeFilter;
      const { data } = await getGroupedRequestLogsApi(params);
      setRows((data.rows || []).map((r) => ({ ...r })));
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, userTypeFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleRowClick = (params) => {
    setSelectedGroup(params.row);
    setModalOpen(true);
  };

  const handleExport = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('User Device Sessions');
    ws.columns = [
      { header: 'User', key: 'user', width: 25 },
      { header: 'Email', key: 'email', width: 30 },
      { header: 'Device', key: 'device', width: 25 },
      { header: 'Brand', key: 'brand', width: 15 },
      { header: 'OS', key: 'os', width: 15 },
      { header: 'Requests', key: 'count', width: 12 },
      { header: 'IP', key: 'ip', width: 18 },
      { header: 'Last Seen', key: 'lastSeen', width: 24 },
    ];
    rows.forEach((r) => {
      ws.addRow({
        user: r.user?.name || r.user?.userName || '—',
        email: r.user?.email || '—',
        device: r.modelName,
        brand: r.brand,
        os: `${r.osName || ''} ${r.osVersion || ''}`.trim(),
        count: r.count,
        ip: r.ip,
        lastSeen: fmtDate(r.latestTimestamp),
      });
    });
    ws.getRow(1).font = { bold: true };
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), 'JAMSAP_UserDeviceSessions.xlsx');
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="User Logs"
        subtitle="Log of all users and devices"
      />

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search user, email"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          size="small" sx={{ flex: 1, minWidth: 200 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            },
          }}
        />

        <Button variant="outlined" startIcon={<FileDownloadIcon />}
          onClick={handleExport} disabled={rows.length === 0} size="small">
          Export
        </Button>

        {/* <TextField
          select label="User Type" value={userTypeFilter}
          onChange={(e) => { setUserTypeFilter(e.target.value); setPage(0); }}
          size="small" sx={{ width: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="user">User</MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
          <MenuItem value="superadmin">Super Admin</MenuItem>
        </TextField> */}
      </Box>

      <DataGrid
        rows={rows}
        columns={GROUPED_COLUMNS}
        pagination
        paginationMode="server"
        rowCount={total}
        pageSizeOptions={[25, 50, 100]}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={({ page: p, pageSize: ps }) => { setPage(p); setPageSize(ps); }}
        loading={loading}
        onRowClick={handleRowClick}
        sx={{
          bgcolor: '#fff', borderRadius: 2, border: '1px solid #e8eaf0',
          '& .MuiDataGrid-row': { cursor: 'pointer' },
          '& .MuiDataGrid-row:hover': { bgcolor: '#f8f8ff' },
        }}
        autoHeight
        disableRowSelectionOnClick
        getRowHeight={() => 56}
      />

      <LogDetailModal
        group={selectedGroup}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );
}